import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../axiosConfig';
import './CartPage.css';
import { getProductImageUrl } from '../utils/productImage';
import { formatSizeSpaced } from '../utils/formatSize';


export default function CartPage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [loading, setLoading] = useState(true);
  // last removed item, kept for a few seconds so the removal can be undone
  const [undoItem, setUndoItem] = useState(null);
  const undoTimer = useRef(null);

  useEffect(() => () => clearTimeout(undoTimer.current), []);

  useEffect(() => {
    const fetchCartProducts = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/api/v1/products?cart=true');
        const fetchedProducts = response.data || [];
        setProducts(fetchedProducts);

        // Initialize quantities from products
        const initialQuantities = {};
        fetchedProducts.forEach((p) => {
          if (p.chosen_variation?.quantity) {
            initialQuantities[p.id] = p.chosen_variation.quantity;
          }
        });
        setQuantities(initialQuantities);
      } catch (error) {
        console.error('Error fetching cart products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCartProducts();
  }, []);

  const incrementQuantity = (product, e) => {
    e.stopPropagation();
    const token = localStorage.getItem('authToken');
    if (!token) {
      return;
    }
    const variationId =
      product.chosen_variation?.id || product.product_variations?.[0]?.id || null;
    axios
      .post(`/api/v1/cart/increase_quantity`, {
        product_id: product.id,
        product_variation_id: variationId,
      })
        .then((response) => {
          setQuantities((prev) => ({ ...prev, [product.id]: response.data.quantity }));
          window.dispatchEvent(new CustomEvent('cartUpdated'));
        })
        .catch((error) => console.error('Error increasing quantity:', error));
  };

  const currentQuantity = (product) =>
    quantities[product.id] !== undefined
      ? quantities[product.id]
      : product.chosen_variation?.quantity || 1;

  const dropFromState = (product) => {
    setProducts((prev) => prev.filter((p) => p.id !== product.id));
    setQuantities((prev) => {
      const next = { ...prev };
      delete next[product.id];
      return next;
    });
  };

  // Remember what was removed (and where it was) and offer "Скасувати" for 5 seconds
  const rememberRemoved = (product, quantity, index) => {
    clearTimeout(undoTimer.current);
    setUndoItem({ product, quantity, index });
    undoTimer.current = setTimeout(() => setUndoItem(null), 5000);
  };

  const removeItem = (product, e) => {
    e.stopPropagation();
    const token = localStorage.getItem('authToken');
    if (!token) return;

    const quantity = currentQuantity(product);
    const index = products.findIndex((p) => p.id === product.id);

    // Remove all items of this product from the cart
    axios
      .post(`/api/v1/cart/toggle_item`, {
        product_id: product.id,
        product_variation_id: null
      })
      .then(() => {
        dropFromState(product);
        window.dispatchEvent(new CustomEvent('cartUpdated'));
        rememberRemoved(product, quantity, index);
      })
      .catch((error) => console.error('Error removing item from cart:', error));
  };

  const undoRemoval = () => {
    if (!undoItem) return;
    const { product, quantity, index } = undoItem;
    const variationId =
      product.chosen_variation?.id || product.product_variations?.[0]?.id || null;

    clearTimeout(undoTimer.current);
    setUndoItem(null);

    axios
      .post(`/api/v1/cart/add_multiple`, {
        items: [{ product_variation_id: variationId, quantity }]
      })
      .then(() => {
        setProducts((prev) => {
          const next = [...prev];
          next.splice(Math.min(index, next.length), 0, product);
          return next;
        });
        setQuantities((prev) => ({ ...prev, [product.id]: quantity }));
        window.dispatchEvent(new CustomEvent('cartUpdated'));
      })
      .catch((error) => console.error('Error restoring item to cart:', error));
  };

  const decrementQuantity = (product, e) => {
    e.stopPropagation();
    const token = localStorage.getItem('authToken');
    if (!token) {
      return;
    }
    const currentQty = quantities[product.id] !== undefined 
      ? quantities[product.id] 
      : product.chosen_variation?.quantity || 1;
    if (currentQty === 1) {
      // Remove from cart
      const variationId =
        product.chosen_variation?.id || product.product_variations?.[0]?.id || null;
      axios
        .post(`/api/v1/cart/decrease_quantity`, {
          product_id: product.id,
          product_variation_id: variationId,
        })
        .then(() => {
          const index = products.findIndex((p) => p.id === product.id);
          dropFromState(product);
          window.dispatchEvent(new CustomEvent('cartUpdated'));
          rememberRemoved(product, 1, index);
        })
        .catch((error) => console.error('Error removing from cart:', error));
      return;
    }
    const variationId =
      product.chosen_variation?.id || product.product_variations?.[0]?.id || null;
    axios
      .post(`/api/v1/cart/decrease_quantity`, {
        product_id: product.id,
        product_variation_id: variationId,
      })
        .then((response) => {
          setQuantities((prev) => ({ ...prev, [product.id]: response.data.quantity }));
          window.dispatchEvent(new CustomEvent('cartUpdated'));
        })
        .catch((error) => console.error('Error decreasing quantity:', error));
  };


  if (loading) {
    return (
      <div className="cart-page">
        <div className="loading-indicator">
          <div className="loader"></div>
        </div>
      </div>
    );
  }

  const openProduct = (product) => navigate(`/products/${product.id}?from=cart`);

  const lineOf = (product) => {
    const quantity = quantities[product.id] !== undefined
      ? quantities[product.id]
      : product.chosen_variation?.quantity || 1;
    const variation = product.chosen_variation ||
      product.product_variations?.find(v => v.main) ||
      product.product_variations?.[0];
    const price = variation ? parseFloat(variation.price) || 0 : 0;
    return { quantity, variation, price };
  };
  const formatMoney = (value) => (value % 1 === 0 ? value.toString() : value.toFixed(2));

  return (
    <div className="cart-page">
      <div className="cart-list">
        {products.length === 0 ? (
          <div className="cart-empty">
            <p>Ваш кошик порожній</p>
            <button className="cart-empty-btn" onClick={() => navigate('/')}>До товарів</button>
          </div>
        ) : (
          <>
            {products.map((product) => {
              const imageUrl = getProductImageUrl(product);
              const { quantity, variation, price } = lineOf(product);
              const totalPrice = price * quantity;

              return (
                <div key={product.id} className="cart-item">
                  <div className="cart-item-image" onClick={() => openProduct(product)}>
                    <img src={imageUrl} alt={product.title} />
                  </div>
                  <div className="cart-item-main">
                    <div className="cart-item-top">
                      <h3 className="cart-item-title" onClick={() => openProduct(product)}>{product.title}</h3>
                      <button
                        className="cart-item-remove"
                        onClick={(e) => removeItem(product, e)}
                        aria-label="Remove item"
                      >
                        ×
                      </button>
                    </div>
                    <div className="cart-item-meta">
                      {variation && variation.size && (
                        <span className="cart-item-variation">{formatSizeSpaced(variation.size)}</span>
                      )}
                      <span className="cart-item-price">{formatMoney(price)} ₴</span>
                    </div>
                    <div className="cart-item-bottom">
                      <div className="cart-item-controls">
                        <button
                          className="cart-quantity-btn cart-quantity-btn--minus"
                          onClick={(e) => decrementQuantity(product, e)}
                          aria-label="Decrease quantity"
                        >
                          −
                        </button>
                        <span className="cart-quantity">{quantity}</span>
                        <button
                          className="cart-quantity-btn cart-quantity-btn--plus"
                          onClick={(e) => incrementQuantity(product, e)}
                          aria-label="Increase quantity"
                        >
                          +
                        </button>
                      </div>
                      <div className="cart-item-total">
                        <p>{formatMoney(totalPrice)} ₴</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
      {undoItem && (
        <div className="cart-undo" role="status">
          <span className="cart-undo__text">Товар видалено</span>
          <button className="cart-undo__btn" onClick={undoRemoval}>Скасувати</button>
        </div>
      )}
    </div>
  );
}
